/**
 * Sprocket application shell.
 *
 * This is deliberately thin: it owns the *loading lifecycle* only — show a
 * splash while Pyodide boots, then swap to a "ready" stage. The actual IDE
 * surfaces (tutor story panel, immediate-mode REPL, later Monaco + file tree +
 * turtle bot) are downstream tasks that will mount into `#stage`.
 *
 * The one piece of real behaviour here is the Pyodide *proof*: once the runtime
 * is live we run `print("hello")` from JS and surface the captured stdout, so
 * later tasks have a known-good output pipeline to wire into the REPL.
 */
import { createPythonRuntime } from "../pyodide/runtime";
import { loadPyodideFromCdn, PYODIDE_CDN_INDEX_URL } from "../pyodide/loader";
import type { LoadPyodideFn, OutputChunk, PythonRuntime } from "../pyodide/types";
import { createLayoutController, type LayoutController } from "./layout-view";
import type { Stage } from "./layout";
import { createLessonEngine, type LessonEngine } from "../lessons/engine";
import { createLessonView } from "../lessons/lesson-view";
import { SAMPLE_CHAPTERS } from "../lessons/content";
import { createReplSession } from "../repl/model";
import { createReplView, type ReplView } from "../repl/repl-view";

export type AppPhase = "loading" | "ready" | "error";

export interface AppElements {
  root: HTMLElement;
  splash: HTMLElement;
  stage: HTMLElement;
  status: HTMLElement;
  output: HTMLElement;
  /** Host the panel/split layout mounts into, once the runtime is ready. */
  layout: HTMLElement;
  /** Stage-switch control group (buttons carry `data-stage`). */
  stageSwitcher: HTMLElement;
}

export interface BootResult {
  runtime: PythonRuntime;
  /** Output captured from the startup `print("hello")` proof. */
  proofOutput: string;
  /** The mounted layout controller, or null if no DOM was provided to mount into. */
  layout: LayoutController | null;
  /** The lesson engine driving the tutor pane, or null if no DOM was provided. */
  lessons: LessonEngine | null;
  /** The immediate-window REPL view, or null if no DOM was provided. */
  immediate: ReplView | null;
}

/** Resolve the DOM nodes the shell drives, throwing early if markup is missing. */
export function queryAppElements(doc: Document): AppElements {
  const get = (id: string): HTMLElement => {
    const el = doc.getElementById(id);
    if (!el) throw new Error(`Sprocket: missing required element #${id}`);
    return el;
  };
  return {
    root: get("app"),
    splash: get("splash"),
    stage: get("stage"),
    status: get("splash-status"),
    output: get("proof-output"),
    layout: get("layout"),
    stageSwitcher: get("stage-switcher"),
  };
}

/**
 * Mount the panel/split layout into `#layout` and wire the stage-switch
 * buttons to it. Returns the controller so callers (and tests) can drive the
 * stage programmatically — this is the simple state API the brief asks for.
 */
export function mountLayout(
  els: AppElements,
  doc: Document,
  initialStage: Stage = 1,
): LayoutController {
  const controller = createLayoutController(doc, { stage: initialStage });
  controller.mount(els.layout);

  // Reflect the active stage onto the switcher (for CSS highlighting) and let
  // each button drive the controller. Buttons opt in via `data-stage`.
  const buttons = Array.from(
    els.stageSwitcher.querySelectorAll<HTMLElement>("[data-stage]"),
  );
  const sync = (stage: Stage): void => {
    els.stageSwitcher.dataset.activeStage = String(stage);
    for (const btn of buttons) {
      btn.dataset.active = btn.dataset.stage === String(stage) ? "true" : "false";
    }
  };
  for (const btn of buttons) {
    btn.addEventListener("click", () => {
      const stage = Number(btn.dataset.stage) as Stage;
      controller.setStage(stage);
    });
  }
  controller.onStageChange(sync);
  sync(controller.stage);

  return controller;
}

/**
 * Mount the authored lesson engine (#702) into the layout's **tutor** pane.
 *
 * The layout (#709) renders a labelled placeholder for each pane whose body
 * carries `data-slot="<paneId>"`; the tutor content slots into
 * `[data-slot="tutor"]`. Because the layout re-renders its whole tree on a
 * stage switch (which recreates the tutor pane element), we re-mount the lesson
 * view on every `onStageChange` — `LessonView.mount` is idempotent, so this is
 * safe and keeps the kid's place. Returns the engine so callers/tests can drive
 * it; null when no tutor slot exists (e.g. pure-runtime test docs).
 */
export function mountTutor(
  els: AppElements,
  doc: Document,
  controller: LayoutController,
): LessonEngine {
  // Engine state (progress, current step) outlives any single pane render, so
  // it is created once; only the view is re-attached to fresh tutor elements.
  const engine = createLessonEngine({ chapters: SAMPLE_CHAPTERS });
  const view = createLessonView(doc, engine);

  const attach = (): void => {
    const slot = els.layout.querySelector?.(
      '[data-slot="tutor"]',
    ) as HTMLElement | null;
    if (slot) view.mount(slot);
  };

  attach();
  controller.onStageChange(attach);
  return engine;
}

/**
 * Mount the immediate-window REPL (#701) into the layout's **immediate** pane.
 *
 * Mirrors {@link mountTutor}: the session/model is created once (so the kid's
 * history + transcript outlive any pane re-render), and the view is re-attached
 * to the fresh `[data-slot="immediate"]` element on every stage switch (mount
 * is idempotent). The REPL is handed the same `engine` the tutor uses, so every
 * run is offered to the current lesson step's success check. Returns the view
 * so callers/tests can drive it; null when no immediate slot exists.
 */
export function mountImmediate(
  els: AppElements,
  doc: Document,
  controller: LayoutController,
  runtime: PythonRuntime,
  engine: LessonEngine,
): ReplView {
  const session = createReplSession();
  const view = createReplView(doc, { runtime, session, engine });

  const attach = (): void => {
    const slot = els.layout.querySelector?.(
      '[data-slot="immediate"]',
    ) as HTMLElement | null;
    if (slot) view.mount(slot);
  };

  attach();
  controller.onStageChange(attach);
  return view;
}

/** Reflect the current lifecycle phase onto the DOM via a data attribute + status text. */
export function setPhase(
  els: AppElements,
  phase: AppPhase,
  message: string,
): void {
  els.root.dataset.phase = phase;
  els.status.textContent = message;
}

/** Options for {@link boot}. */
export interface BootOptions {
  /** Override the Pyodide loader (used by tests). Defaults to the CDN loader. */
  loadPyodide?: LoadPyodideFn;
  indexURL?: string;
  /** Document used to build the layout DOM. Defaults to the global `document`. */
  doc?: Document;
  /** Initial layout stage (default 1). */
  stage?: Stage;
}

/**
 * Boot the runtime, run the startup proof, then mount the progressive layout.
 * Pure-ish: it takes the elements and options, mutates the DOM to reflect
 * progress, and returns the runtime + mounted layout controller so callers (and
 * tests) can keep driving them.
 */
export async function boot(
  els: AppElements,
  options: BootOptions = {},
): Promise<BootResult> {
  const loadPyodide = options.loadPyodide ?? loadPyodideFromCdn;
  const indexURL = options.indexURL ?? PYODIDE_CDN_INDEX_URL;
  const doc =
    options.doc ?? (typeof document !== "undefined" ? document : undefined);

  setPhase(els, "loading", "Waking up the robot brain…");

  const runtime = await createPythonRuntime({ loadPyodide, indexURL });

  // Collect output from the proof run. We subscribe before running so nothing
  // is missed, then unsubscribe to keep the runtime clean for later consumers.
  const chunks: string[] = [];
  const unsubscribe = runtime.onOutput((chunk: OutputChunk) => {
    chunks.push(chunk.text);
  });
  try {
    await runtime.run('print("hello")');
  } finally {
    unsubscribe();
  }
  const proofOutput = chunks.join("").trimEnd();

  els.output.textContent = proofOutput;
  setPhase(
    els,
    "ready",
    `Ready! Running CPython ${runtime.pythonVersion} in your browser.`,
  );

  // Now that the runtime is live, mount the progressive layout shell. The
  // panes are labelled placeholders; downstream tasks slot real content in by
  // pane id. Skipped only when no Document is available (pure-runtime tests).
  const layout = doc ? mountLayout(els, doc, options.stage ?? 1) : null;

  // With the layout (and its tutor placeholder) live, mount the authored
  // lesson engine into the tutor pane. Only when we have both a Document and a
  // layout controller to host it.
  const lessons = doc && layout ? mountTutor(els, doc, layout) : null;

  // Finally, mount the immediate-window REPL into the layout's immediate pane.
  // It shares the tutor's lesson engine so running code can satisfy exercises.
  const immediate =
    doc && layout && lessons
      ? mountImmediate(els, doc, layout, runtime, lessons)
      : null;

  return { runtime, proofOutput, layout, lessons, immediate };
}
